import { Component, OnInit, OnDestroy, ElementRef, ViewChild, Input} from '@angular/core';
import { Router, NavigationStart } from "@angular/router";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Subscription, of, Observable, forkJoin } from "rxjs";
import { filter, map, mergeMap, tap } from 'rxjs/operators';

import { FlowIndex } from "../../../../common/models/flow.dto";
import { PendingFlow } from '../../../../flow/models/flow.model';

import { FlowInstanceService } from "../../../../flow/services/flow-instance.service";
import { WorkOrderService } from "../../../../common/services/work-order.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { AuthService } from "../../../../services/auth.service";
import { dateComparator } from "../../../../common/components/data-table/comparators/data-table.comparators";
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";
import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";
import { TabComponent } from "../../../../common/components/tabs/tab.component";

import { ContactStub, Contact } from "../../../../admin/models/contact.model";
import { Group } from "../../../../admin/models/group.model";
import { GroupSearchSpec, GroupService } from "../../../../admin/services/group.service";
import { ActionCellRendererComponent } from "../../../../common/components/ag-grid/action-cell-renderer.component";
import { PendingWorkOrderAssignment } from '../../../../common/models/work-order.model';
import { LunaClaimValues, UserIdentity } from '../../../../models/user-identity.model';
import { ViewBase } from "../view-base";


@Component({
    selector: 'organization-management-internal-widget',
    templateUrl: './organization-management-internal-widget.component.html',
    styleUrls: ['./organization-management-internal-widget.component.scss'],
    standalone: false
})
export class OrganizationManagementInternalWidgetComponent extends ViewBase implements OnInit, OnDestroy {
    @Input() widgetTitle: string;


    allUsers: ContactStub[] = [];
    groups: Group[] = [];
    firstFilteredUsers: ContactStub[] = [];
    finalFilteredUsers: ContactStub[] = [];
    faFilteredUsers: ContactStub[] = [];
    groupHasPermission: any;



    applicationNumberColumn: ColDef = {
        editable: false,
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "flowIdentifier",
        tooltipField: "flowIdentifier",
        minWidth: 145,
        maxWidth: 160,  
        cellRendererSelector: (params) => {
            if (this.hasWorkOrderPermission(params.data)) {
                return {
                    component: RouterLinkRendererComponent,
                    params: {
                        textField: "flowIdentifier",
                        flowIdField: "flowId",
                        workItemIdField: "currentWorkItemId"
                    }
                };
            } else {
                return {
                    component: ModalInfoComponent,
                    params: {
                        modalText: (data) => { return "You do not have permission to view this flow."; },
                        text: (data) => { return (data.flowIdentifier != null && data.flowIdentifier.length > 0) ? data.flowIdentifier : "Unknown"; }
                    }
                };
            }
        },
        filter: "agTextColumnFilter"
    };

    applicationTypeColumn: ColDef = {
        editable: false,
        headerName: "Application Type",
        headerTooltip: "Application Type",
        field: "name",
        tooltipField: "name",
        maxWidth: 225,
        minWidth: 200,
        filter: "agTextColumnFilter"
    } as ColDef;

    approvedDateColumn: ColDef = {
        editable: false,
        headerName: "Approved",
        headerTooltip: "Approved",
        field: "approvedDate",
        valueGetter: this.formatter.formatDate("approvedDate"),
        tooltipValueGetter: this.formatter.formatDate("approvedDate"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 135,
        minWidth: 125
    } as ColDef;

    approvedColumns: ColDef[] = [];
    approvedFlows: FlowIndex[];
	approvedGridOptions: GridOptions;
	approvedGridApi?: GridApi;

    companyColumn: ColDef = {
        editable: false,
        headerName: "Company",
        headerTooltip: "Company",
        field: "companyName",
        tooltipField: "companyName",
        filter: "agTextColumnFilter",
        minWidth: 125,
        maxWidth: 150
    } as ColDef;

    completeDateColumn: ColDef = {
        editable: false,
        headerName: "Completed",
        headerTooltip: "Completed",
        field: "approvedDate",
        valueGetter: this.formatter.formatDate("approvedDate"),
        tooltipValueGetter: this.formatter.formatDate("approvedDate"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 135,
        minWidth: 125
    } as ColDef;

    completeColumns: ColDef[] = [];
    completeFlows: FlowIndex[];
    completeGridOptions: GridOptions;
	completedGridApi?: GridApi;

    deniedColumns: ColDef[] = [];
    deniedFlows: FlowIndex[];
	deniedGridOptions: GridOptions;
	deniedGridApi?: GridApi;

    deniedDateColumn: ColDef = {
        editable: false,
        headerName: "Denied",
        headerTooltip: "Denied",
        field: "rejectedDate",
        valueGetter: this.formatter.formatDate("rejectedDate"),
        tooltipValueGetter: this.formatter.formatDate("rejectedDate"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 135,
        minWidth: 125
    } as ColDef;

	faGridOptions: GridOptions;
	faGridApi?: GridApi;
    faFlows: FlowIndex[];

    finalReviewFlows: FlowIndex[];
    finalReviewGridOptions: GridOptions;
	finalReviewGridApi?: GridApi;

    firstReviewFlows: FlowIndex[];
	firstReviewGridOptions: GridOptions;
	firstReviewGridApi?: GridApi;

    legalColumns: ColDef[] = [];
	legalGridOptions: GridOptions;
	legalGridApi?: GridApi;
    legalReviewFlows: FlowIndex[];


    noLinkApplicationNumberColumn: ColDef = {
        editable: false,
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "flowIdentifier",
        tooltipField: "flowIdentifier",
        minWidth: 145,
        maxWidth: 160,
        filter: "agTextColumnFilter"
    } as ColDef;

    readonlyLinkApplicationNumberColumn: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            textField: "flowIdentifier",
            openInNewWindow: false,
            url: (data: FlowIndex) => {
                const prefix = (data.onBehalf) ? "/flow/review-on-behalf" : "/flow/review";
                return `${prefix}/${data.flowId}`;
            }
        },
        editable: false,
        headerName: "Application #",
        headerTooltip: "Application #",
        field: "flowIdentifier",
        tooltipField: "flowIdentifier",
        minWidth: 145,
        maxWidth: 160,
        filter: "agTextColumnFilter"
    }

    returnedColumns: ColDef[] = [];
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
    returnedFlows: FlowIndex[];

    submittedColumn: ColDef = {
        editable: false,
        headerName: "Submitted",
        headerTooltip: "Submitted",
        field: "submittedDate",
        valueGetter: this.formatter.formatDate("submittedDate"),
        tooltipValueGetter: this.formatter.formatDate("submittedDate"),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        maxWidth: 140,
        minWidth: 130
    } as ColDef;

    subscription: Subscription = new Subscription();

    constructor(
        private formatter: GridFormatterService,
        private authService: AuthService,
        private dataService: FlowInstanceService,
        private workOrderService: WorkOrderService,
        public groupService: GroupService,
        private router: Router

    ) {
        super("ViewInternalOrganizationManagementWidget", [LunaClaimValues.ViewInternalOrganizationManagementWidget]);

    }

    ngOnInit() {
        let obs$ = this.fetchData("legal").subscribe(
           {
                next: () => { console.log(`tab legal data refreshed`) },
                error: (error) => { console.error(error); }
           }
        );
        this.subscription.add(obs$);
        this.setGrids();
        this.refreshData();

        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscription.unsubscribe();
        });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    assign(params: any): any {
        let result: boolean = false;
        if (params && params.newValue && params.newValue.id) {
            if (params.newValue.id != "-1") {
                params.data.assignedUserId = params.newValue.id;
            } else {
                params.data.assignedUserId = "";
            }

            if (params.context && params.context.componentParent) {
                let parent = <OrganizationManagementInternalWidgetComponent>params.context.componentParent;
                let selectedItem = parent.allUsers.find(u => u.id == params.newValue.id);
                if (selectedItem) {
                    params.data.assignedUserName = selectedItem.displayName;
                }
            }
            result = true;
        }
        return result;
    }
    
    buildFinalReviewColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.applicationNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.applicationTypeColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.finalFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            onCellValueChanged: this.cellValueChanged,
            minWidth: 140
        } as ColDef);
        this.formatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    buildFirstReviewColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.applicationNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.applicationTypeColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.firstFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            onCellValueChanged: this.cellValueChanged,
            minWidth: 140
        } as ColDef);
        this.formatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    buildReturnedReviewColumns(): ColDef[] {
        let colDefs: ColDef[] = [];
        colDefs.push(this.submittedColumn);
        colDefs.push(this.applicationNumberColumn);
        colDefs.push(this.companyColumn);
        colDefs.push(this.applicationTypeColumn);
        colDefs.push({
            headerName: "Assignee",
            headerTooltip: "Assignee",
            field: "assignedUserId",
            suppressFiltersToolPanel: true,
            suppressMovable: true,
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorPopup: true,
            cellEditorParams: {
                formatValue: (value: any) => value?.displayName ?? 'Unassigned',
                values: this.faFilteredUsers
            },
            cellRenderer: this.renderAssignedUser,
            valueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId) ?? null,
            filterValueGetter: (params) => this.allUsers.find(u => u.id === params.data?.assignedUserId)?.displayName ?? 'Unassigned',
            valueSetter: this.assign,
            tooltipValueGetter: this.renderAssignedUser,
            onCellValueChanged: this.cellValueChanged,
            minWidth: 140
        } as ColDef);
        this.formatter.applyFilterKeyboardHintTooltips(colDefs);
        return colDefs;
    }

    cellValueChanged(params: any): void {
        if (params.api) {
            params.api.refreshCells();
        }
    }

    onTabClicked(tab: TabComponent): void {
        const title = tab.title.toLocaleLowerCase();
        let obs$: Observable<FlowIndex[]> = this.fetchData(title);
        let subs$ = obs$.subscribe(
            {
                next: () => { console.log(`tab ${title} data refreshed`) },
                error: (error) => { console.error(error); }
            }
        );
        this.subscription.add(subs$);
    }

    getDataObs$(): Observable<[PendingFlow[], PendingFlow[], PendingFlow[]]> {
        this.showLoadingOverlays(true);

        let allUserSearchObs$ = this.groupService.get({ internal: true, stubs: false, fullContactDetails: true } as GroupSearchSpec);
        let firstSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtFirstReview", false).pipe(tap((data: PendingFlow[]) => {
            this.firstReviewFlows;
        }));
        let finalSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtFinalReview", false).pipe(tap((data: PendingFlow[]) => {
            this.finalReviewFlows;
        }));
        let returnedSearchObs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtAwaitingFA", false).pipe(tap((data: PendingFlow[]) => {
            this.faFlows;
        }));

        let level1Subs$ = allUserSearchObs$.pipe(mergeMap((groupData) => {
            this.allUsers = [];
            this.firstFilteredUsers = [];
            this.finalFilteredUsers = [];
            this.faFilteredUsers = [];
            if (groupData) {
                if (groupData instanceof Array) {
                    this.groups = groupData as Group[];
                    this.groups.forEach(g => this.allUsers = this.allUsers.concat(g.users));
                }
                this.allUsers = this.filterUniqueUsers(this.allUsers);
                this.allUsers.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
            }
            return forkJoin(firstSearchObs$, finalSearchObs$, returnedSearchObs$);
        }));
        return level1Subs$;

    }
    
    buildUserList(groups: Group[]): ContactStub[] {
        let userList: ContactStub[] = [];
        groups.forEach(u => userList = userList.concat(u.users));
        userList = this.filterUniqueUsers(userList);
        userList = this.sortUsers(userList);
        userList.unshift({ id: "-1", displayName: "Unassigned" } as ContactStub);
        return userList;
    }
    
    filterUniqueUsers(users: ContactStub[]): ContactStub[] {
        return users.filter((user, i, arr) => arr.findIndex(t => t.id === user.id) === i);
    }

    hasWorkOrderPermission(data: PendingFlow): boolean {
        let permitted: boolean = false;
        if (data.assignedGroupId != null && data.assignedGroupId.length > 0 && this.authService.User.groups.includes(data.assignedGroupId)) permitted = true;
        else if (data.assignedUserId != null && data.assignedUserId.length > 0 && data.assignedUserId == this.authService.User.id) permitted = true;
        return permitted;
    }
    
    refreshData(): void {
        let subs$ = this.getDataObs$().pipe(
            map(([level1Rows, level2Rows, level3Rows]) => {
                let level1Groups = this.groups.filter(g => this.groupHasPermission(g) && level1Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                let level2Groups = this.groups.filter(g => this.groupHasPermission(g) && level2Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                let level3Groups = this.groups.filter(g => this.groupHasPermission(g) && level3Rows.map(r => r.assignedGroupId).findIndex(r => r == g.id) != null);
                this.firstFilteredUsers = this.buildUserList(level1Groups);
                this.finalFilteredUsers = this.buildUserList(level2Groups);
                this.faFilteredUsers = this.buildUserList(level3Groups);
            })
        ).subscribe({
            next: () => {
                if (this.firstReviewGridApi)
                    this.firstReviewGridApi.setGridOption("columnDefs", []);
                if (this.firstReviewGridApi)
                    this.firstReviewGridApi.setGridOption("columnDefs", this.buildFirstReviewColumns());
                if (this.finalReviewGridApi)
                    this.finalReviewGridApi.setGridOption("columnDefs", []);
                if (this.finalReviewGridApi)
                    this.finalReviewGridApi.setGridOption("columnDefs", this.buildFinalReviewColumns());
                if (this.faGridApi)
                    this.faGridApi.setGridOption("columnDefs", this.buildReturnedReviewColumns());
            }, 
            error: (error) => {
                console.error(error);
            }
        });
        this.subscription.add(subs$);
    }
    
    renderUserDropdownOptions(params: any): string {
        let returnValue = params && params.value && params.value.displayName ? params.value.displayName : "Unassigned";
        return returnValue;
    }
    
    renderAssignedUser(params: any): string {
        let returnValue: string = "Unassigned";
        if (params && params.data && params.data.assignedUserId && params.data.assignedUserName) {
            returnValue = params.data.assignedUserName;
        }
        return returnValue;
    }
    
    saveAssignments(event: any): void {
        let changedItem = <PendingFlow>event.data;
        if (event.context && event.context.componentParent) {
            const component = <OrganizationManagementInternalWidgetComponent>event.context.componentParent;
            const pending = new PendingWorkOrderAssignment(changedItem?.currentWorkItemId ?? "", changedItem.assignedUserId, changedItem.assignedUserName, true, changedItem.flowId);
            let updateSubs$ = component.workOrderService.assignWorkOrder(pending).pipe()
                .subscribe(
                    (result) => { console.log("update managed"); },
                    (error) => { console.error(error); });
            component.subscription.add(updateSubs$);
        }
    }
    
    showLoadingOverlays(show: boolean): void {
        if (this.finalReviewGridOptions && this.finalReviewGridApi) {
            if (show) {
                this.finalReviewGridApi.setGridOption("rowData",[]);
                this.finalReviewGridApi.setGridOption("loading", true);
            } else
                this.finalReviewGridApi.setGridOption("loading", false);
        }
        if (this.firstReviewGridOptions && this.firstReviewGridApi) {
            if (show) {
                this.firstReviewGridApi.setGridOption("rowData",[]);
                this.firstReviewGridApi.setGridOption("loading", true);
            } else
                this.firstReviewGridApi.setGridOption("loading", false);
        }
        if (this.faGridOptions && this.faGridApi) {
            if (show) {
                this.faGridApi.setGridOption("rowData",[]);
                this.faGridApi.setGridOption("loading", true);
            } else
                this.faGridApi.setGridOption("loading", false);
        }
    }
    
    sortUsers(users: ContactStub[]): ContactStub[] {
        return users.sort((a, b) => {
            if (a.firstName > b.firstName) return 1;
            if (a.firstName < b.firstName) return -1;
            if (a.firstName == b.firstName) {
                if (a.lastName > b.lastName) return 1;
                if (a.lastName < b.lastName) return -1;
            }
            return 0;
        })
    }

    fetchData(tabName: string): Observable<FlowIndex[]> {
        let obs$: Observable<FlowIndex[]>;
        switch (tabName.toLocaleLowerCase()) {
            case "approved":
                if (this.approvedFlows && this.approvedFlows.length > 0) {
                    obs$ = of(this.approvedFlows).pipe(tap((data) => { this.approvedGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.dataService.queryFlowIndices("OrganizationMgmtApproved").pipe(tap((approved: FlowIndex[]) => {
                        this.approvedFlows = approved;
                        this.approvedGridApi.sizeColumnsToFit();
                    }));
                }               
                break;
            case "awaiting fa return":
                if (this.faFlows && this.faFlows.length > 0) {
                    obs$ = of(this.faFlows).pipe(tap((data) => { this.faGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtAwaitingFA", false).pipe(tap((fa: PendingFlow[]) => {
                        this.faFlows = fa;
                        this.faGridApi.sizeColumnsToFit();
                    }));
                }             
                break;
            case "complete":
                if (this.completeFlows && this.completeFlows.length > 0) {
                    obs$ = of(this.completeFlows).pipe(tap((data) => { this.completedGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.dataService.queryFlowIndices("OrganizationMgmtComplete").pipe(tap((complete: FlowIndex[]) => {
                        this.completeFlows = complete;
                        this.completedGridApi.sizeColumnsToFit();
                    }));
                }               
                break;
            case "denied":
                if (this.deniedFlows && this.deniedFlows.length > 0) {
                    obs$ = of(this.deniedFlows).pipe(tap((data) => { this.deniedGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.dataService.queryFlowIndices("OrganizationMgmtDenied").pipe(tap((denied: FlowIndex[]) => {
                        this.deniedFlows = denied;
                        this.deniedGridApi.sizeColumnsToFit();
                    }));
                }               
                break;
            case "final":
                if (this.finalReviewFlows && this.finalReviewFlows.length > 0) {
                    obs$ = of(this.finalReviewFlows).pipe(tap((data) => { this.finalReviewGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtFinalReview", false).pipe(tap((finalReview: PendingFlow[]) => {
                        this.finalReviewFlows = finalReview;
                        this.finalReviewGridApi.sizeColumnsToFit();
                    }));
                }                
                break;
            case "first":
                if (this.firstReviewFlows && this.firstReviewFlows.length > 0) {
                    obs$ = of(this.firstReviewFlows).pipe(tap((data) => { this.firstReviewGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtFirstReview", false).pipe(tap((firstReview: PendingFlow[]) => {
                        this.firstReviewFlows = firstReview;
                        this.firstReviewGridApi.sizeColumnsToFit();
                    }));
                }               
                break;
            case "legal":
                if (this.legalReviewFlows && this.legalReviewFlows.length > 0) {
                    obs$ = of(this.legalReviewFlows).pipe(tap((data) => { this.legalGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.workOrderService.getPendingFlowsByTargetTransition("OrganizationMgmtLegalReview_NoFilter", false).pipe(tap((legal: PendingFlow[]) => {
                        this.legalReviewFlows = legal;
                        this.legalGridApi.sizeColumnsToFit();
                    }));
                }                
                break;
            case "returned":
                if (this.returnedFlows && this.returnedFlows.length > 0) {
                    obs$ = of(this.returnedFlows).pipe(tap((data) => { this.returnedGridApi.sizeColumnsToFit() }));
                } else {
                    obs$ = this.dataService.getReturnedIncompleteApplication("OrganizationMgmtReturned", true).pipe(tap((returned: FlowIndex[]) => {
                        this.returnedFlows = returned;
                        this.returnedGridApi.sizeColumnsToFit();
                    }));
                }               
                break;
        }
        return obs$;
    }

    setGrids(): void {
        this.legalColumns.push(this.submittedColumn);
        this.legalColumns.push(this.applicationNumberColumn);
        this.legalColumns.push(this.companyColumn);
        this.legalColumns.push(this.applicationTypeColumn);
        this.legalGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.legalColumns, false, true, true, true);
		this.legalGridOptions.suppressColumnVirtualisation = true;
		this.legalGridOptions.onGridReady = (params) => { this.legalGridApi = params.api; };
        this.firstReviewGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildFirstReviewColumns(), false, true, true, true);
		this.firstReviewGridOptions.suppressColumnVirtualisation = true;
		this.firstReviewGridOptions.context = { componentParent: this };
		this.firstReviewGridOptions.onCellValueChanged = ($event) => this.saveAssignments($event);
		this.firstReviewGridOptions.onGridReady = (params) => { this.firstReviewGridApi = params.api; };
        this.finalReviewGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildFinalReviewColumns(), false, true, true, true);
		this.finalReviewGridOptions.suppressColumnVirtualisation = true;
		this.finalReviewGridOptions.context = { componentParent: this };
		this.finalReviewGridOptions.onCellValueChanged = ($event) => this.saveAssignments($event);
		this.finalReviewGridOptions.onGridReady = (params) => { this.finalReviewGridApi = params.api };
        this.returnedColumns.push(this.submittedColumn);
        this.returnedColumns.push(this.readonlyLinkApplicationNumberColumn);
        this.returnedColumns.push(this.companyColumn);
        this.returnedColumns.push(this.applicationTypeColumn);
        this.returnedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.returnedColumns, false, true, true, true);
		this.returnedGridOptions.suppressColumnVirtualisation = true;
		this.returnedGridOptions.onGridReady = (params) => { this.returnedGridApi = params.api };
        this.approvedColumns.push(this.approvedDateColumn);
        this.approvedColumns.push(this.noLinkApplicationNumberColumn);
        this.approvedColumns.push(this.companyColumn);
        this.approvedColumns.push(this.applicationTypeColumn);
        this.approvedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.approvedColumns, false, true, true, true);
		this.approvedGridOptions.suppressColumnVirtualisation = true;
		this.approvedGridOptions.onGridReady = (params) => { this.approvedGridApi = params.api; };
        this.deniedColumns.push(this.deniedDateColumn);
        this.deniedColumns.push(this.noLinkApplicationNumberColumn);
        this.deniedColumns.push(this.companyColumn);
        this.deniedColumns.push(this.applicationTypeColumn);
        this.deniedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.deniedColumns, false, true, true, true);
		this.deniedGridOptions.suppressColumnVirtualisation = true;
		this.deniedGridOptions.onGridReady = (params) => { this.deniedGridApi = params.api; };
        this.faGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildReturnedReviewColumns(), false, true, true, true);
        this.faGridOptions.suppressColumnVirtualisation = true;
        this.faGridOptions.context = { componentParent: this };
        this.faGridOptions.onCellValueChanged = ($event) => this.saveAssignments($event);
        this.faGridOptions.onGridReady = (params) => { this.faGridApi = params.api; };
        this.completeColumns.push(this.completeDateColumn);
        this.completeColumns.push(this.noLinkApplicationNumberColumn);
        this.completeColumns.push(this.companyColumn);
        this.completeColumns.push(this.applicationTypeColumn);
        this.completeGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.completeColumns, false, true, true, true);
		this.completeGridOptions.suppressColumnVirtualisation = true;
		this.completeGridOptions.onGridReady = (params) => { this.completedGridApi = params.api; };
    }
}
